import {
  getStaticContentHost,
  joinStaticContentUrl,
} from "utils/staticContent/staticContent";

const isAbsoluteUrl = (value: string): boolean =>
  value.startsWith("http://") || value.startsWith("https://");

const stripQueryAndHash = (value: string): string =>
  value.split(/[?#]/, 1)[0] ?? "";

/**
 * Normalises a content-block `<img src>` value to a static-bucket key.
 *
 * @remarks
 * Supported inputs:
 * - `img/<name>` (preferred stored form)
 * - legacy stored form: `<name>` (treated as `img/<name>`)
 * - absolute URLs pointing at:
 *   - `VITE_STATIC_CONTENT_HOST` (`https://static.hellocashflow.com/img/<name>`)
 *
 * External URLs are intentionally ignored (return `null`) so the editor does not
 * rewrite third-party images.
 *
 * @see docs/guides/STATIC_CONTENT.md
 */
export const normaliseContentBlockImgKey = (rawSrc: string): string | null => {
  const trimmed = rawSrc.trim();
  if (!trimmed || trimmed.startsWith("data:")) {
    return null;
  }

  const withoutQuery = stripQueryAndHash(trimmed);

  if (withoutQuery.startsWith("img/")) {
    return withoutQuery;
  }
  if (withoutQuery.startsWith("/img/")) {
    return withoutQuery.slice(1);
  }

  // Legacy stored form: `<img src="filename.png">`
  if (!isAbsoluteUrl(withoutQuery) && !withoutQuery.includes("/")) {
    return `img/${withoutQuery}`;
  }

  if (!isAbsoluteUrl(withoutQuery)) {
    return null;
  }

  const staticHost = getStaticContentHost();
  if (!staticHost.startsWith("/")) {
    try {
      const url = new URL(withoutQuery);
      const staticHostname = new URL(staticHost).hostname;

      if (url.hostname === staticHostname) {
        const key = url.pathname.startsWith("/")
          ? url.pathname.slice(1)
          : url.pathname;
        return key.startsWith("img/") ? key : null;
      }
    } catch {
      // fall through
    }
  }

  // Intentionally do not rewrite non-static absolute URLs. Any legacy host values should
  // be normalised at write-time (admin/backend) and via DB migration.
  // See docs/guides/STATIC_CONTENT.md.
  return null;
};

const replaceImgSrcAttributes = (
  html: string,
  replaceSrc: (src: string) => string | null,
): string =>
  html.replace(
    /(<img\b[^>]*\bsrc=")([^"]+)(")/gi,
    (match, prefix: string, src: string, suffix: string) => {
      const next = replaceSrc(src);
      return next ? `${prefix}${next}${suffix}` : match;
    },
  );

export const rewriteContentBlockHtmlForEditor = (html: string): string =>
  replaceImgSrcAttributes(html, (src) => {
    const key = normaliseContentBlockImgKey(src);
    return key ? joinStaticContentUrl(key) : null;
  });

export const rewriteContentBlockHtmlForStorage = (html: string): string =>
  replaceImgSrcAttributes(html, (src) => normaliseContentBlockImgKey(src));
